import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
  type MutableRefObject,
} from "react";
import { useForm, type UseFormReturn, type FieldValues } from "react-hook-form";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import useCallbackRef from "@shared/hooks/useCallbackRef/useCallbackRef";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { useTranslation } from "@shared/utils/internationalization";
import { normalizeAddress } from "@shared/modules/basket/utils/address-utils";
import { formatCheckoutFormsToBasketEndpointsPayload } from "@shared/modules/checkout/utils/format-checkout-forms";
import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import PublicCheckoutBillingAddressForm from "@app-contexts/public/shared/checkout/components/steps/review/delivery-methods/PublicCheckoutBillingAddressForm/PublicCheckoutBillingAddressForm.web";

import { usePaymentManager } from "../../../contexts/PaymentManagerContext";
import YunoPaymentGateway from "../../../gateways/YunoPaymentGateway/YunoPaymentGateway";
import type { PurchaseOptsType } from "../../../types";
import { createPaymentLogger } from "../../../utils/payment-logger";
import {
  NO_WALLET_METHODS,
  WalletElementSharedProps,
} from "../../WalletElement/WalletElement.props";

const log = createPaymentLogger("YunoWalletElement");
const notifier = NotifierFactory.createNotifier();

const APPLE_PAY_CONTAINER_ID = "yuno-apple-pay-button";
const GOOGLE_PAY_CONTAINER_ID = "yuno-google-pay-button";

// Yuno's SDK mounts wallet buttons asynchronously into DOM containers but
// doesn't report which wallets are available via a callback. We use
// MutationObservers to detect when a clickable wallet button appears
// anywhere inside the Apple Pay / Google Pay containers, then settle
// after a short window.
const YUNO_WALLET_SETTLE_MS = 500;

// How Yuno actually renders these buttons (inspected from the v1.5 SDK
// bundle at sdk-web.y.uno):
//   - Apple Pay: Yuno calls document.createElement("apple-pay-button") —
//     Apple's own custom element — only after ApplePaySession.canMakePayments()
//     returns true. Not a native <button>, so a plain "button" selector
//     would miss it.
//   - Google Pay: Yuno delegates to Google's pay.js (PaymentsClient.createButton),
//     which renders a <div> containing a <button> (and sometimes an iframe).
// Yuno also toggles an inner wrapper between display:none and display:flex
// depending on availability, so we additionally verify the candidate has
// a non-zero bounding rect (descendants of display:none have rect 0).
const WALLET_BUTTON_SELECTOR =
  'button, [role="button"], a[href], iframe, apple-pay-button';

function hasClickableWalletButton(container: HTMLElement): boolean {
  const candidates = Array.from(
    container.querySelectorAll<HTMLElement>(WALLET_BUTTON_SELECTOR)
  );
  return candidates.some((el) => {
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-disabled") === "true") return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

type WalletDetectionCleanup = () => void;
const noop: WalletDetectionCleanup =
  Function.prototype as WalletDetectionCleanup;

function detectYunoWalletMethods(
  onDetected: (methods: { applePay: boolean; googlePay: boolean }) => void
): WalletDetectionCleanup {
  const appleEl = document.getElementById(APPLE_PAY_CONTAINER_ID);
  const googleEl = document.getElementById(GOOGLE_PAY_CONTAINER_ID);

  if (!appleEl || !googleEl) {
    onDetected(NO_WALLET_METHODS);
    return noop;
  }

  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  const observers: MutationObserver[] = [];

  const report = (): void => {
    onDetected({
      applePay: hasClickableWalletButton(appleEl),
      googlePay: hasClickableWalletButton(googleEl),
    });
  };

  const scheduleSettle = (): void => {
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(report, YUNO_WALLET_SETTLE_MS);
  };

  // If buttons are already rendered (e.g. reinitialize), report immediately.
  if (hasClickableWalletButton(appleEl) || hasClickableWalletButton(googleEl)) {
    report();
    return noop;
  }

  // Observe the full subtree — Yuno may mount wrapper nodes first and
  // insert the actual button/iframe deeper in the tree afterwards.
  // eslint-disable-next-line no-restricted-syntax
  for (const el of [appleEl, googleEl]) {
    const observer = new MutationObserver(scheduleSettle);
    observer.observe(el, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled"],
    });
    observers.push(observer);
  }

  // Kick off the settle timer so we still report if neither container
  // ever receives a clickable button (wallet not available on this device).
  scheduleSettle();

  return () => {
    if (settleTimer) clearTimeout(settleTimer);
    // eslint-disable-next-line no-restricted-syntax
    for (const obs of observers) obs.disconnect();
  };
}

interface YunoBillingFormSectionProps {
  formRef: MutableRefObject<UseFormReturn<FieldValues> | null>;
  onBillingValidityChange?: (isValid: boolean) => void;
}

/**
 * Extracted billing form so useForm() only runs when the manual addresses
 * flag is active. Communicates the form instance back to the parent via
 * formRef so buildPurchaseOpts can read billing data at authorization time.
 */
function YunoBillingFormSection({
  formRef,
  onBillingValidityChange,
}: YunoBillingFormSectionProps): JSX.Element {
  const billingForm = useForm({ mode: "onChange" });
  // eslint-disable-next-line no-param-reassign -- standard mutable ref pattern
  formRef.current = billingForm;

  const onBillingValidityChangeRef = useCallbackRef(onBillingValidityChange);

  const onBillingValid = useCallback(() => {
    onBillingValidityChangeRef.current?.(true);
  }, []);

  const onBillingInvalid = useCallback(() => {
    onBillingValidityChangeRef.current?.(false);
  }, []);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line no-param-reassign -- standard mutable ref cleanup
      formRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleValidationTriggered = (): void => {
      billingForm.trigger();
    };
    window.addEventListener(
      "checkout:validation-triggered",
      handleValidationTriggered
    );
    return () => {
      window.removeEventListener(
        "checkout:validation-triggered",
        handleValidationTriggered
      );
    };
  }, [billingForm]);

  return (
    <Box position="relative" zIndex={2} mt="md" mb="md">
      <PublicCheckoutBillingAddressForm
        form={billingForm}
        onValidFields={onBillingValid}
        onInvalidFields={onBillingInvalid}
      />
    </Box>
  );
}

interface YunoWalletElementProps extends WalletElementSharedProps {
  paymentVenue?: number;
}

/**
 * Self-contained Yuno wallet element that mounts Apple Pay / Google Pay buttons,
 * manages an optional billing address form (when manual addresses are enabled),
 * and orchestrates the purchase call when the Yuno SDK authorizes a wallet payment.
 *
 * Follows the same call-time-opts pattern as StripeWalletElement: the component
 * subscribes to the gateway's authorization callback, builds purchase opts at
 * that moment, and calls manager.purchase() directly.
 *
 * The billing form is extracted into YunoBillingFormSection so useForm() only
 * runs when the manual addresses flag is active. The child communicates the
 * form instance back via a ref so buildPurchaseOpts can read it at call time.
 */
function YunoWalletElement({
  onElementReady,
  onElementError,
  onComponentUnmount,
  onPaymentSuccess,
  onAvailablePaymentMethods,
  paymentVenue,
}: YunoWalletElementProps): JSX.Element {
  const { manager } = usePaymentManager();
  const { isFlagActive } = useDjangoWaffle();
  const { t } = useTranslation("checkout");
  const gatewayRef = useRef<YunoPaymentGateway | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [preservedHeight, setPreservedHeight] = useState<number | null>(null);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [isBillingValid, setIsBillingValid] = useState(true);

  const hasMountedRef = useRef(false);
  const isReinitializingRef = useRef(false);
  const walletDetectionCleanupRef = useRef<WalletDetectionCleanup | null>(null);

  const onElementReadyRef = useCallbackRef(onElementReady);
  const onElementErrorRef = useCallbackRef(onElementError);
  const onComponentUnmountRef = useCallbackRef(onComponentUnmount);
  const onPaymentSuccessRef = useCallbackRef(onPaymentSuccess);
  const onAvailablePaymentMethodsRef = useCallbackRef(
    onAvailablePaymentMethods
  );
  const isManualAddressesEnabled = isFlagActive(
    "enable_yuno_wallet_manual_addresses",
    paymentVenue
  );

  const needsBillingBlock =
    isManualAddressesEnabled && (!showBillingForm || !isBillingValid);

  const handleBillingValidityChange = useCallback((isValid: boolean): void => {
    setIsBillingValid(isValid);
  }, []);

  const handleBillingOverlayClick = useCallback((): void => {
    if (!showBillingForm) {
      setShowBillingForm(true);
      return;
    }
    notifier.error(t("Please provide a valid billing address"));
    window.dispatchEvent(new CustomEvent("checkout:validation-triggered"));
  }, [showBillingForm, t]);

  const billingFormRef = useRef<UseFormReturn<FieldValues> | null>(null);

  const buildPurchaseOpts = useCallback((): PurchaseOptsType => {
    // Clear any existing shipping address — the wallet confirmation SDK
    // (Apple Pay / Google Pay) captures shipping addresses itself.
    const opts: PurchaseOptsType = { shipping_address: null };
    if (!isManualAddressesEnabled) return opts;

    const form = billingFormRef.current;
    if (!form) return opts;
    const billing = formatCheckoutFormsToBasketEndpointsPayload(form);
    const normalizedBilling = normalizeAddress(billing);
    if (normalizedBilling) {
      opts.billing_address = normalizedBilling;
    }
    return opts;
  }, [isManualAddressesEnabled]);

  // --- Wallet purchase orchestration ---
  // Subscribes to the gateway's onWalletPaymentAuthorized callback, builds
  // opts at authorization time, and calls manager.purchase() directly.
  useEffect(() => {
    const gateway = manager.handler?.gateway;
    if (!(gateway instanceof YunoPaymentGateway)) return undefined;

    const handler = async (oneTimeToken: string): Promise<void> => {
      const purchaseOpts = buildPurchaseOpts();
      purchaseOpts.wallet_one_time_token = oneTimeToken;

      try {
        const result = await manager.purchase(
          PAYMENT_MANAGER_CONFIRMATION_STRATEGY.WALLET,
          purchaseOpts
        );
        onPaymentSuccessRef.current?.(result);
      } catch {
        manager.onProcessing?.(false);
      }
    };

    gateway.onWalletPaymentAuthorized = handler;
    return () => {
      if (gateway.onWalletPaymentAuthorized === handler) {
        gateway.onWalletPaymentAuthorized = undefined;
      }
    };
  }, [manager.handler?.gateway, buildPurchaseOpts]);

  // --- Button mount / unmount ---
  const createAndMountElement = useCallback(async () => {
    log.info("Mounting");

    const gateway = manager.handler?.gateway;
    if (!(gateway instanceof YunoPaymentGateway)) {
      log.info("Requires YunoPaymentGateway — skipping mount");
      onElementErrorRef.current?.();
      return;
    }

    if (!gateway.instance) {
      log.info("Yuno instance not available for mount");
      onElementErrorRef.current?.();
      return;
    }

    if (hasMountedRef.current) {
      return;
    }

    gatewayRef.current = gateway;
    hasMountedRef.current = true;

    try {
      await gateway.instance.mountExternalButtons([
        {
          paymentMethodType: "APPLE_PAY",
          elementSelector: `#${APPLE_PAY_CONTAINER_ID}`,
        },
        {
          paymentMethodType: "GOOGLE_PAY",
          elementSelector: `#${GOOGLE_PAY_CONTAINER_ID}`,
        },
      ]);
      setPreservedHeight(null);
      isReinitializingRef.current = false;
      onElementReadyRef.current?.();

      walletDetectionCleanupRef.current?.();
      walletDetectionCleanupRef.current = detectYunoWalletMethods((methods) =>
        onAvailablePaymentMethodsRef.current?.(methods)
      );
    } catch (error) {
      isReinitializingRef.current = false;
      log.error("Failed to mount wallet buttons", {
        error: error as Record<string, unknown>,
      });
      onAvailablePaymentMethodsRef.current?.(NO_WALLET_METHODS);
      onElementErrorRef.current?.();
    }
  }, [manager.handler?.gateway]);

  const unmount = useCallback(() => {
    log.info("Unmounting");

    const gateway = manager.handler?.gateway;
    if (!(gateway instanceof YunoPaymentGateway)) {
      log.info("Requires YunoPaymentGateway — skipping unmount");
      onElementErrorRef.current?.();
      return;
    }

    if (!gateway.instance) {
      log.info("Yuno instance not available for unmount");
      onElementErrorRef.current?.();
      return;
    }

    gateway.instance.unmountAllExternalButtons();
    walletDetectionCleanupRef.current?.();
    walletDetectionCleanupRef.current = null;

    hasMountedRef.current = false;
    if (!isReinitializingRef.current) {
      onComponentUnmountRef.current?.();
    }
  }, [manager.handler?.gateway]);

  const willReinitializeHandlerRef = useRef<() => void>();
  willReinitializeHandlerRef.current = () => {
    const height = containerRef.current?.getBoundingClientRect().height;
    if (height && height > 0) {
      setPreservedHeight(height);
    }
    isReinitializingRef.current = true;
    unmount();
  };

  const didReinitializeHandlerRef = useRef<() => void>();
  didReinitializeHandlerRef.current = () => {
    createAndMountElement();
  };

  useLayoutEffect(() => {
    createAndMountElement();
    return () => unmount();
  }, [createAndMountElement, unmount]);

  useEffect(() => {
    const gateway = manager.handler?.gateway;
    if (!(gateway instanceof YunoPaymentGateway)) return undefined;

    const unsubWillReinit = gateway.onWillReinitialize(() => {
      willReinitializeHandlerRef.current?.();
    });

    const unsubDidReinit = gateway.onInitialization(() => {
      didReinitializeHandlerRef.current?.();
    });

    return () => {
      unsubWillReinit();
      unsubDidReinit();
    };
  }, [manager.handler?.gateway]);

  return (
    <>
      {isManualAddressesEnabled && showBillingForm && (
        <YunoBillingFormSection
          formRef={billingFormRef}
          onBillingValidityChange={handleBillingValidityChange}
        />
      )}
      <Flex
        ref={containerRef}
        position="relative"
        direction="row"
        gap="xs"
        width="100%"
        style={
          preservedHeight ? { minHeight: `${preservedHeight}px` } : undefined
        }
      >
        <Box
          id={APPLE_PAY_CONTAINER_ID}
          // eslint-disable-next-line showpass-custom/prefer-semantic-props -- intentional special case
          css={{
            flex: "1",
            "&:empty": {
              display: "none",
            },
          }}
        />
        <Box
          id={GOOGLE_PAY_CONTAINER_ID}
          // eslint-disable-next-line showpass-custom/prefer-semantic-props -- intentional special case
          css={{
            flex: "1",
            "&:empty": {
              display: "none",
            },
          }}
        />
        {needsBillingBlock && (
          <Box
            position="absolute"
            inset="none"
            zIndex={1}
            cursor="pointer"
            onClick={handleBillingOverlayClick}
          />
        )}
      </Flex>
    </>
  );
}

export default YunoWalletElement;
